import { getPlantDataForVessel, getVesselDisplayName } from "@components/map/utils/vesselMetadata"
import { InformationContainer } from "@components/containers/Containers"
import FeatureGuard from "@components/featureGuard/FeatureGuard"
import PositionDataKpi from "@components/positionDataKpi/PositionDataKpi"
import SectionTitle from "@components/sectionTitle/SectionTitle"
import { SectionTitleSize, SectionTitleType } from "@components/sectionTitle/types"
import ShipImgContainer from "@components/shipImgContainer/ShipImgContainer"
import { capitalizeEachWord } from "@helpers/capitalize"
import { findDeviationSeverity } from "@helpers/findDeviationSeverity"
import * as Tabs from "@radix-ui/react-tabs"
import { RequestStatus } from "@redux/app/RequestStatus"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getListOfVessels } from "@redux/features/listOfVessels/listOfVesselsSlice"
import {
	getPanelState,
	updateSidePanelState,
} from "@redux/features/mapSidePanelControl/mapSidePanelControlSlice"
import { getOfflineVessels } from "@redux/features/offlineVessels/offlineVesselsSlice"
import {
	clearSelectedVessel,
	getSelectedVessel,
	setSelectedVessel,
} from "@redux/features/selectedVessel/selectedVesselSlice"
import { getUnderConfigurationVessels } from "@redux/features/underConfigurationVessels/underConfigurationVesselsSlice"
import { getUniSeaVessels } from "@redux/features/uniSeaVessels/uniSeaVesselsSlice"
import { useGetWarningCondition, useGetWarningDialogHeight } from "@sop/DashboardPage/DashboardPage"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { IconButton, LoadingCircle, Tooltip } from "@ui-lib/src"
import { OpModeState } from "@utilityTypes/opmodeDefs"
import { common } from "@utils/common"
import { PortalFeature } from "api"
import { DateTime } from "luxon"
import { ReactElement, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
	MdAutoStories,
	MdClose,
	MdFeed,
	MdSignalWifiStatusbarConnectedNoInternet,
	MdSignalWifiStatusbarNotConnected,
} from "react-icons/md"
import { getCoordinatesFormated } from "./CoordLine"
import LocalityTimeline from "./LocalityTimeline/LocalityTimeline"
import VesselSpecSheetContent, { VesselSpecSheetContentSmall } from "./VesselSpecSheetContent"
import { fake_data_demo, fake_data_demo_vard, TableListOfPlants } from "./table/TableListOfPlants"
import { getHardCodedVesselData } from "./table/hard_coded_vessel_data"
import FishKpis from "./vesselKpis/FishKpis"
import FuelAndPowerKpis from "./vesselKpis/FuelAndPowerKpis"
import WaterQualityKpis from "./vesselKpis/WaterQualityKpis"
import { getVesselSpecSheetData } from "./vesselsSpecSheetData"

export default function VesselsContentNL({
	showHeader = true,
	onUserManualClick,
}: {
	showHeader?: boolean
	onUserManualClick?: () => void
}): ReactElement | null {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { vessel } = useSearch({ strict: false }) //Route.useSearch()

	const dispatch = useAppDispatch()
	const listOfVessels = useAppSelector(getListOfVessels)
	const offlineVessels = useAppSelector(getOfflineVessels)
	const uniSeaVessels = useAppSelector(getUniSeaVessels)
	const underConfigurationVessels = useAppSelector(getUnderConfigurationVessels)

	const panelState = useAppSelector(getPanelState)
	const selectedVessel = useAppSelector(getSelectedVessel)
	const companyName = useAppSelector(getCurrentCompany)

	useEffect(() => {
		if (vessel === undefined) {
			if (selectedVessel.name !== "") {
				dispatch(clearSelectedVessel())
			}
		} else {
			const vesselData = listOfVessels.vessels.find(
				(v) =>
					v.name.toLowerCase() === vessel.toLowerCase() ||
					getVesselDisplayName(v, companyName).toLowerCase() === vessel.toLowerCase() ||
					v.plantId.toString() === vessel
			)
			if (vesselData !== undefined) {
				const vesselDisplayName = getVesselDisplayName(vesselData, companyName)
				if (panelState.isTableExpanded === true) {
					dispatch(
						updateSidePanelState({
							...panelState,
							isTableExpanded: false,
						})
					)
				}
				if (
					selectedVessel.plantId !== vesselData.plantId ||
					selectedVessel.name !== vesselDisplayName
				) {
					dispatch(setSelectedVessel({ ...vesselData, name: vesselDisplayName }))
				}
			}
		}
	}, [
		companyName,
		dispatch,
		listOfVessels,
		panelState,
		selectedVessel.name,
		selectedVessel.plantId,
		vessel,
	])

	const fetchSelectedVesselDataStatus = useAppSelector((state) => state.selectedVessel.status)
	const plantData =
		selectedVessel.name !== "" && companyName !== undefined
			? getPlantDataForVessel(selectedVessel, companyName)
			: undefined
	const latestOpMode = selectedVessel.latestOperationMode
	const latestOpMode2 = selectedVessel.latestOperationMode2

	const VESSELS_SPEC_SHEET_DATA = getVesselSpecSheetData(companyName)
	const selectedVesselSpecSheetData = VESSELS_SPEC_SHEET_DATA[selectedVessel.name]
	const vesselOfflineData = offlineVessels?.find((vesselData) => vesselData.name === vessel)

	const vesselUniSeaData = uniSeaVessels?.find((vesselData) => vesselData.name === vessel)
	const vesselUnderConfigurationData = underConfigurationVessels?.find(
		(vesselData) => vesselData.name === vessel
	)
	const [painelContainerRef, setPainelContainerRef] = useState<HTMLDivElement | null>(null)
	const warningCondition = useGetWarningCondition()
	const warningDialogHeight = useGetWarningDialogHeight()

	const hardCodedVesselData =
		companyName === "Dipai Portal Demo"
			? fake_data_demo
			: companyName === "Vard Demo"
				? fake_data_demo_vard
				: getHardCodedVesselData(companyName)

	const listOfVesselTypes = new Set([
		...listOfVessels.vessels
			.map((v) => [
				hardCodedVesselData.find((data) => data.vessel === v.name)?.capacity?.name,
				hardCodedVesselData.find((data) => data.vessel === v.name)?.type,
			])
			// .filter(
			// 	(type): type is [string, string] =>
			// 		type[0] !== undefined &&
			// 		type[0] !== "--" &&
			// 		type[1] !== undefined &&
			// 		type[1] !== "--"
			// )
			.map((type) => type[1]),
		...(listOfVessels.vessels.map((v) => v.plantId).includes(1178) ? ["REV"] : []),
	])

	const [selectedVesselType, setSelectedVesselType] = useState<string>(
		listOfVesselTypes.size > 1 ||
			(listOfVesselTypes.size === 1 && listOfVesselTypes.values().next().value === undefined)
			? `0`
			: `1`
	)

	useEffect(() => {
		setSelectedVesselType(
			listOfVesselTypes.size > 1 ||
				(listOfVesselTypes.size === 1 &&
					listOfVesselTypes.values().next().value === undefined)
				? `0`
				: `1`
		)
	}, [[...Array.from(listOfVesselTypes), companyName].toString()])

	console.log("🚀 ~ selectedVessel:", selectedVessel)
	return companyName !== undefined ? (
		<div
			ref={(newRef) => setPainelContainerRef(newRef)}
			className="grid w-full overflow-x-auto"
		>
			{selectedVessel.name === "" ? (
				<div
					id="map-table"
					className="h-screen w-full overflow-auto"
				>
					{showHeader !== false ? (
						<div className="bg-dipai-secondary-901 flex h-13 w-full items-center justify-between px-4">
							<p
								className={
									"text-title5 flex items-center gap-2 font-bold text-white"
								}
							>
								{t("MAP")}
							</p>
							<div
								className="animate-[bounce_1s_4] text-base text-white/70 hover:cursor-pointer hover:text-white/90"
								onClick={onUserManualClick}
							>
								<MdAutoStories className={`size-6`} />
							</div>
						</div>
					) : null}
					<div
						style={{
							height:
								warningCondition === true
									? `calc(100vh - 52px - ${warningDialogHeight}px)`
									: `calc(100vh - 52px)`,
						}}
						className={`overflow-auto pr-4`}
					>
						|
						<Tabs.Root
							orientation="horizontal"
							className=""
							value={selectedVesselType}
							onValueChange={(e) => setSelectedVesselType(e)}
						>
							<div
								className={`text-dipai-text-primary border-dipai-text-disabled mt-2 -mb-1 ml-4 flex h-auto flex-row border-b-1`}
							>
								<p className={`pr-8 font-bold`}>{`${t("FLEET")} `}</p>
								<Tabs.List
									aria-label="tabs example"
									className={`text-menu mb-0 flex min-h-10 w-125 flex-wrap gap-x-6`}
								>
									{listOfVesselTypes.size > 1 ||
									(listOfVesselTypes.size === 1 &&
										listOfVesselTypes.values().next().value === undefined) ? (
										<Tabs.Trigger
											value={`0`}
											className={`data-[state=active]:border-dipai-text-highlight data-[state=active]:text-dipai-text-highlight data-[state=inactive]:hover:text-dipai-text-highlight data-[state=inactive]:border-dipai-text-highlight/0 flex border-b-2 font-medium data-[state=active]:hover:cursor-default data-[state=inactive]:hover:cursor-pointer`}
										>
											{capitalizeEachWord(t("ALL"))}
										</Tabs.Trigger>
									) : null}
									{Array.from(listOfVesselTypes)
										.filter((t) => t !== undefined)
										.map((vesselType, index) => (
											<Tabs.Trigger
												key={vesselType}
												value={`${index + 1}`}
												className={`data-[state=active]:border-dipai-text-highlight data-[state=active]:text-dipai-text-highlight data-[state=inactive]:hover:text-dipai-text-highlight data-[state=inactive]:border-dipai-secondary-700/0 flex border-b-2 font-medium data-[state=active]:hover:cursor-default data-[state=inactive]:hover:cursor-pointer`}
											>{`${vesselType}`}</Tabs.Trigger>
										))}
								</Tabs.List>
							</div>
							{listOfVesselTypes.size > 1 ||
							(listOfVesselTypes.size === 1 &&
								listOfVesselTypes.values().next().value === undefined) ? (
								<Tabs.Content value={`0`}>
									<div className={`mt-3 mb-3 pl-4`}>
										<div className={`relative`}>
											<TableListOfPlants customerName={companyName} />
										</div>
									</div>
								</Tabs.Content>
							) : null}
							{Array.from(listOfVesselTypes)
								.filter((t) => t !== undefined)
								.map((vesselType, index) => (
									<Tabs.Content
										key={vesselType}
										value={`${index + 1}`}
									>
										<div className={`mt-3 mb-3 pl-4`}>
											<div className={`relative`}>
												<TableListOfPlants
													customerName={companyName}
													vesselType={vesselType}
												/>
											</div>
										</div>
									</Tabs.Content>
								))}
						</Tabs.Root>
					</div>
				</div>
			) : plantData !== undefined ? (
				<>
					<div className="bg-dipai-secondary-901 flex h-13 w-full items-center justify-between border-b-1 border-white px-4">
						<p className="text-headline flex items-center gap-4 text-white">
							{selectedVessel.name}
							{companyName.toLowerCase().includes(" demo") === false &&
								selectedVessel.vessel?.mmsi !== undefined && (
									<span className="text-tiny pl-2">{`(MMSI: ${selectedVessel.vessel.mmsi})`}</span>
								)}
						</p>

						<div className={`flex flex-row gap-2`}>
							<Tooltip
								collisionBoundary={painelContainerRef}
								customContent={t("CLOSE")}
							>
								<IconButton
									size={"sm"}
									variant={"text-light"}
									onClick={() => {
										dispatch(clearSelectedVessel())
										navigate({
											to: ".",
											search: (prev) => ({
												...prev,
												vessel: undefined,
											}),
										})
									}}
								>
									<IconButton.Icon>{MdClose}</IconButton.Icon>
								</IconButton>
							</Tooltip>
						</div>
					</div>
					{vesselUnderConfigurationData !== undefined ? (
						<div className="text-tiny mx-1 mt-1 mb-1 flex h-auto w-[calc(100%-8px)] flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
							<MdSignalWifiStatusbarNotConnected
								className={`h-5 w-5 text-slate-700`}
							/>
							<div
								className={`border-gray-ccc flex flex-row items-center gap-1 rounded-full border-0`}
							>
								<div className={``}>
									<p className={``}>{`${t(
										"VESSEL IS CURRENTLY UNDER CONFIGURATION"
									)}`}</p>
									<p className={``}>{`${t(
										"SOME/ALL PORTAL FEATURES MIGHT BE UNAVAILABLE"
									)}`}</p>
								</div>
							</div>
						</div>
					) : null}
					{vesselUniSeaData !== undefined ? (
						<div className="text-tiny mx-1 mt-1 mb-1 flex h-auto w-[calc(100%-8px)] flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
							<MdFeed className={`h-5 w-5 text-blue-400`} />
							<div
								className={`border-gray-ccc flex flex-row items-center gap-1 rounded-full border-0`}
							>
								<div className={``}>
									<p className={``}>{`${t(
										"ONLY UNISEA DATA IS AVAILABLE FOR THIS VESSEL"
									)}`}</p>
									<p>
										{`Data ${t(`AVAILABLE`)} ${t(`FROM`).toLowerCase()}: ${
											new Date(vesselUniSeaData.firstTimestamp)
												.toString()
												.split(" (")[0]
										}`}
									</p>
								</div>
							</div>
						</div>
					) : null}
					{vesselOfflineData !== undefined && vesselUniSeaData === undefined ? (
						<div className={`border-b-1 p-1`}>
							<div className="text-tiny flex h-auto w-full flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
								<MdSignalWifiStatusbarConnectedNoInternet
									className={`h-5 w-5 text-amber-700`}
								/>
								<div className={``}>
									{vesselOfflineData.lastUpdateTimestamp !== null ? (
										<>
											<p>{t(`Vessel is currently offline.`)}</p>
											<p>
												{t(
													`Last update: ${
														new Date(
															vesselOfflineData.lastUpdateTimestamp
														)
															.toString()
															.split(" (")[0]
													}`
												)}
											</p>
										</>
									) : (
										<p>{t(`No data found for this vessel.`)}</p>
									)}
								</div>
							</div>
						</div>
					) : null}
					<div
						style={{
							height:
								vesselOfflineData !== undefined
									? companyName?.toLowerCase() !== "dipai"
										? `calc(100vh - 52px - 59px - ${warningDialogHeight}px)`
										: `calc(100vh - 52px - 52px - 59px - ${warningDialogHeight}px)`
									: companyName?.toLowerCase() !== "dipai"
										? `calc(100vh - 52px - ${warningDialogHeight}px)`
										: `calc(100vh - 52px - 52px - ${warningDialogHeight}px)`,
						}}
						className={`w-full overflow-auto`}
					>
						<ShipImgContainer
							shipName={selectedVessel.name}
							companyName={companyName}
						/>
						<SectionTitle
							title={t("VESSEL INFORMATION").toUpperCase()}
							type={SectionTitleType.vessel}
							size={SectionTitleSize.medium}
						/>
						<InformationContainer>
							<div
								className={`text-button grid-rows text-dipai-secondary-901 grid gap-y-2`}
							>
								<div
									className={`grid grid-cols-[100px_1fr_100px_1fr] gap-x-2 border-b-1`}
								>
									<p className={`pb-2 font-semibold`}>{t("VESSEL TYPE")}</p>
									<p className={`text-dipai-secondary-901/90 mr-4`}>
										{hardCodedVesselData.find(
											(data) => data.vessel === selectedVessel.name
										)?.type ?? "Unknown"}
									</p>
									<p className={`pb-2 font-semibold`}>
										{hardCodedVesselData.find(
											(data) => data.vessel === selectedVessel.name
										)?.capacity?.name ?? t("CAPACITY")}
									</p>
									<p className={`text-dipai-secondary-901/90`}>
										{`
									${hardCodedVesselData.find((data) => data.vessel === selectedVessel.name)?.capacity?.value ?? "--"} 
									${hardCodedVesselData.find((data) => data.vessel === selectedVessel.name)?.capacity?.unit ?? ""}`}
									</p>
								</div>
								<div className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1`}>
									<p className={`pb-2 font-semibold`}>{t("OPERATION")}</p>
									{latestOpMode2 !== undefined ? (
										<p className={`text-dipai-secondary-901/90`}>
											{latestOpMode2.opModeName}
										</p>
									) : latestOpMode !== undefined &&
									  ((latestOpMode?.opModeEvents !== undefined &&
											latestOpMode?.opModeEvents.length > 0) ||
											latestOpMode?.openState !== undefined) ? (
										<>
											{latestOpMode.opModeEvents !== undefined &&
												latestOpMode?.opModeEvents.length > 0 && (
													<div className="flex gap-4 pb-2">
														<p className="text-menu text-dipai-secondary-901/90 w-30">
															{"Operation"}
														</p>
														<div className="flex flex-col gap-2">
															{latestOpMode?.opModeEvents.map(
																(opModeEvent, index) => (
																	<div
																		key={`OpMode_${index}`}
																		className="flex items-center gap-2"
																	>
																		<div
																			className={`h-3 w-3 rounded-full ${
																				findDeviationSeverity(
																					selectedVessel
																						?.latestOperationMode
																						?.deviationEvents ??
																						[],
																					opModeEvent.subsystemId
																				) ===
																				OpModeState.RED
																					? "bg-alert-error"
																					: findDeviationSeverity(
																								selectedVessel
																									?.latestOperationMode
																									?.deviationEvents ??
																									[],
																								opModeEvent.subsystemId
																						  ) ===
																						  OpModeState.YELLOW
																						? "bg-alert-warning"
																						: findDeviationSeverity(
																									selectedVessel
																										?.latestOperationMode
																										?.deviationEvents ??
																										[],
																									opModeEvent.subsystemId
																							  ) ===
																							  OpModeState.GREEN
																							? "bg-alert-sucess"
																							: "bg-gray-666"
																			}`}
																		/>
																		<p className="text-menu text-dipai-secondary-901/90 cursor-pointer underline">
																			{`${opModeEvent.subsystemName}: ${opModeEvent.opModeName}`}
																		</p>
																	</div>
																)
															)}
														</div>
													</div>
												)}
											{latestOpMode?.openState !== undefined && (
												<div className="flex gap-4 border-t-1 pt-2 pb-2">
													<p className="text-dipai-secondary-901/90 w-30">
														{"Open to sea"}
													</p>

													<p className="text-dipai-secondary-901/90 pl-5">
														{latestOpMode.openState}
													</p>
												</div>
											)}
											{/* {latestOpMode.wellControl
												?.length > 0 && (
												<div className="flex gap-4 pt-2 border-t-1">
													<p className="w-30 text-menu text-dipai-secondary-901/90">
														{"Well Control"}
													</p>
													<div
														className={`grid-rows-[${latestOpMode.wellControl.length} grid]`}
													>
														{latestOpMode.wellControl.map(
															(opMode, index) => (
																<div
																	key={index}
																	className="flex items-center gap-2"
																>
																	<p
																		key={index}
																		className="pl-5 text-menu text-dipai-secondary-901/90"
																	>
																		{`${
																			opMode.zone.split(
																				"ActiveQuick"
																			)[1] ??
																			"Unidentified Zone"
																		}: ${opMode.mode}`}
																	</p>
																</div>
															)
														)}
													</div>
												</div>
											)} */}
										</>
									) : (
										<p>{t("UNKNOWN OPERATION MODE")}</p>
									)}
								</div>
								<div className={`grid grid-cols-[100px_1fr] gap-x-2`}>
									<p className={`font-semibold`}>{t("CHARTERER")}</p>
									<p className={`text-dipai-secondary-901/90`}>
										{hardCodedVesselData.find(
											(data) => data.vessel === selectedVessel.name
										)?.charterer ?? "Unknown"}
									</p>
								</div>
							</div>
						</InformationContainer>
						{companyName?.toLowerCase() !== "dipai" ? (
							<FeatureGuard
								featureName={PortalFeature.VESSEL_KPIS}
								plant={plantData}
							>
								<>
									<SectionTitle
										title={"POWER AND FUEL"}
										type={SectionTitleType.vessel}
										size={SectionTitleSize.medium}
									/>
									<FuelAndPowerKpis
										plantId={plantData.id}
										customerName={companyName}
									/>
								</>
							</FeatureGuard>
						) : null}
						{companyName?.toLowerCase() !== "dipai" ? (
							<FeatureGuard
								featureName={PortalFeature.FISH_KPIS}
								plant={plantData}
							>
								<>
									<SectionTitle
										title={"FISH DATA"}
										type={SectionTitleType.vessel}
										size={SectionTitleSize.medium}
									/>
									<FishKpis customerName={companyName} />
								</>
							</FeatureGuard>
						) : null}
						{companyName?.toLowerCase() !== "dipai" ? (
							<FeatureGuard
								featureName={PortalFeature.WATER_QUALITY_KPIS}
								plant={plantData}
							>
								<>
									<SectionTitle
										title={"WATER QUALITY"}
										type={SectionTitleType.vessel}
										size={SectionTitleSize.medium}
									/>
									<WaterQualityKpis customerName={companyName} />
								</>
							</FeatureGuard>
						) : null}
						<SectionTitle
							title={t("POSITION DATA").toUpperCase()}
							type={SectionTitleType.vessel}
							size={SectionTitleSize.medium}
						/>
						<InformationContainer>
							{selectedVessel.position !== undefined ? (
								<>
									<div className="mb-2 grid grid-cols-3 divide-x">
										<PositionDataKpi
											label={t("SPEED")}
											unit={"knots"}
											value={selectedVessel.position!.sog}
										/>
										<PositionDataKpi
											label={t("VESSEL HEADING")}
											unit={"°"}
											value={selectedVessel.position!.heading}
										/>
										<PositionDataKpi
											label={t("VESSEL COURSE")}
											unit={"°"}
											value={selectedVessel.position!.cog}
										/>
									</div>
									<div className="grid-rows text-small grid border-t-1">
										<div
											className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1 py-1`}
										>
											<p className={`text-dipai-secondary-901 font-semibold`}>
												{t("LAST UPDATE")}
											</p>
											<p className={`text-dipai-secondary-901/90`}>
												{DateTime.fromISO(
													selectedVessel.position!.timeStamp
												).toFormat("cccc, LLL dd, tt")}
											</p>
										</div>
										<div
											className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1 py-1`}
										>
											<p className={`text-dipai-secondary-901 font-semibold`}>
												{t("DESTINATION")}
											</p>
											<p className={`text-dipai-secondary-901/90`}>
												{selectedVessel?.vessel?.destination !== undefined
													? selectedVessel.vessel.destination
													: t(`NO DESTINATION INFORMATION AVAILABLE`)}
											</p>
										</div>
										<div className={`grid grid-cols-[100px_1fr] gap-x-2 pt-1`}>
											<p className={`text-dipai-secondary-901 font-semibold`}>
												{t("COORDINATES")}
											</p>
											<p className={`text-dipai-secondary-901/90`}>
												{selectedVessel.position?.lat !== undefined &&
												selectedVessel.position?.lon !== undefined
													? getCoordinatesFormated(
															selectedVessel.position?.lat !==
																undefined
																? selectedVessel.position?.lat.toString()
																: undefined,
															selectedVessel.position?.lon !==
																undefined
																? selectedVessel.position?.lon.toString()
																: undefined
														)
													: t(`NO COORDINATE INFORMATION AVAILABLE`)}
											</p>
										</div>
									</div>
								</>
							) : (
								<p> {"No AIS data available"} </p>
							)}
						</InformationContainer>
						{selectedVesselSpecSheetData !== undefined ? (
							<VesselSpecSheetContent
								vesselSpecSheetData={selectedVesselSpecSheetData}
							/>
						) : null}
						{companyName?.toLowerCase() !== "dipai" ? (
							<FeatureGuard
								featureName={PortalFeature.TIMELINE}
								plant={plantData}
							>
								<>
									<SectionTitle
										title={"VESSEL TIMELINE"}
										type={SectionTitleType.vessel}
										size={SectionTitleSize.medium}
									/>
									{fetchSelectedVesselDataStatus === RequestStatus.loading ? (
										<InformationContainer>
											<LoadingCircle containerHeightWithUnit={"300px"} />
										</InformationContainer>
									) : (
										<InformationContainer>
											<LocalityTimeline />
										</InformationContainer>
									)}
								</>
							</FeatureGuard>
						) : null}
					</div>
				</>
			) : (
				<p>Vessel data not found</p>
			)}
		</div>
	) : null
}

export const VesselDetails = ({
	vessel,
	handleClose,
}: {
	vessel: string
	handleClose: () => void
}) => {
	const { t } = useTranslation()
	const warningDialogHeight = useGetWarningDialogHeight()

	const listOfVessels = useAppSelector(getListOfVessels)
	const selectedVesselData = listOfVessels.vessels.find((v) => v.name === vessel)
	const latestOpMode = selectedVesselData?.latestOperationMode
	const latestOpMode2 = selectedVesselData?.latestOperationMode2
	const position = selectedVesselData?.position

	const offlineVessels = useAppSelector(getOfflineVessels)
	const uniSeaVessels = useAppSelector(getUniSeaVessels)
	const underConfigurationVessels = useAppSelector(getUnderConfigurationVessels)

	const companyName = useAppSelector(getCurrentCompany)
	const VESSELS_SPEC_SHEET_DATA = getVesselSpecSheetData(companyName)
	const selectedVesselSpecSheetData = VESSELS_SPEC_SHEET_DATA[vessel]
	const vesselOfflineData = offlineVessels?.find((vesselData) => vesselData.name === vessel)

	const vesselUniSeaData = uniSeaVessels?.find((vesselData) => vesselData.name === vessel)
	const vesselUnderConfigurationData = underConfigurationVessels?.find(
		(vesselData) => vesselData.name === vessel
	)

	const hardCodedVesselData =
		companyName === "Dipai Portal Demo"
			? fake_data_demo
			: companyName === "Vard Demo"
				? fake_data_demo_vard
				: getHardCodedVesselData(companyName)

	return (
		<div className="grid w-full overflow-x-auto">
			<div className="bg-dipai-secondary-901 flex h-8 w-full items-center justify-between rounded-t-sm border-b-1 border-white px-2">
				<p className="text-body font-medium text-white">
					{vessel}
					{companyName !== undefined &&
						companyName.toLowerCase().includes(" demo") === false &&
						selectedVesselData?.vessel?.mmsi !== undefined && (
							<span className="text-tiny pl-2">{`(MMSI: ${selectedVesselData.vessel.mmsi})`}</span>
						)}
				</p>

				<IconButton
					size={"sm"}
					variant={"text-light"}
					onClick={handleClose}
				>
					<IconButton.Icon>{MdClose}</IconButton.Icon>
				</IconButton>
			</div>
			{vesselUnderConfigurationData !== undefined ? (
				<div className="text-tiny mx-1 mt-1 mb-1 flex h-auto w-[calc(100%-8px)] flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
					<MdSignalWifiStatusbarNotConnected className={`h-5 w-5 text-slate-700`} />
					<div
						className={`border-gray-ccc flex flex-row items-center gap-1 rounded-full border-0`}
					>
						<div className={``}>
							<p className={``}>{`${t(
								"VESSEL IS CURRENTLY UNDER CONFIGURATION"
							)}`}</p>
							<p className={``}>{`${t(
								"SOME/ALL PORTAL FEATURES MIGHT BE UNAVAILABLE"
							)}`}</p>
						</div>
					</div>
				</div>
			) : null}
			{vesselUniSeaData !== undefined ? (
				<div className="text-tiny mx-1 mt-1 mb-1 flex h-auto w-[calc(100%-8px)] flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
					<MdFeed className={`h-5 w-5 text-blue-400`} />
					<div
						className={`border-gray-ccc flex flex-row items-center gap-1 rounded-full border-0`}
					>
						<div className={``}>
							<p className={``}>{`${t(
								"ONLY UNISEA DATA IS AVAILABLE FOR THIS VESSEL"
							)}`}</p>
							<p>
								{`Data ${t(`AVAILABLE`)} ${t(`FROM`).toLowerCase()}: ${
									new Date(vesselUniSeaData.firstTimestamp)
										.toString()
										.split(" (")[0]
								}`}
							</p>
						</div>
					</div>
				</div>
			) : null}
			{vesselOfflineData !== undefined && vesselUniSeaData === undefined ? (
				<div className={`border-b-1 p-1`}>
					<div className="text-tiny flex h-auto w-full flex-row items-center gap-3 rounded-sm border-1 border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-700">
						<MdSignalWifiStatusbarConnectedNoInternet
							className={`h-5 w-5 text-amber-700`}
						/>
						<div className={``}>
							{vesselOfflineData.lastUpdateTimestamp !== null ? (
								<>
									<p>{t(`Vessel is currently offline.`)}</p>
									<p>
										{t(
											`Last update: ${
												new Date(vesselOfflineData.lastUpdateTimestamp)
													.toString()
													.split(" (")[0]
											}`
										)}
									</p>
								</>
							) : (
								<p>{t(`No data found for this vessel.`)}</p>
							)}
						</div>
					</div>
				</div>
			) : null}
			<div
				style={{
					maxHeight:
						vesselOfflineData !== undefined
							? companyName?.toLowerCase() !== "dipai"
								? `calc(100vh - 54px - 52px - 59px - ${warningDialogHeight}px)`
								: `calc(100vh - 54px - 52px - 52px - 59px - ${warningDialogHeight}px)`
							: companyName?.toLowerCase() !== "dipai"
								? `calc(100vh - 54px - 52px - ${warningDialogHeight}px)`
								: `calc(100vh - 54px - 52px - 52px - ${warningDialogHeight}px)`,
				}}
				className={`w-full overflow-auto`}
			>
				<ShipImgContainer
					shipName={vessel}
					companyName={companyName}
				/>
				<div className="bg-dipai-secondary-901 flex h-7 w-full items-center justify-between px-2">
					<p className="text-menu font-medium text-white">{t("VESSEL INFORMATION")}</p>
				</div>
				<div className={`text-tiny grid-rows grid gap-y-0 p-2`}>
					<div className={`grid grid-cols-[100px_1fr_100px_1fr] gap-x-2 border-b-1`}>
						<p className={`text-dipai-secondary-901 pb-1 font-semibold`}>
							{t("VESSEL TYPE")}
						</p>
						<p className={`text-dipai-secondary-901/90 mr-4`}>
							{hardCodedVesselData.find((data) => data.vessel === vessel)?.type ??
								"Unknown"}
						</p>
						<p className={`text-dipai-secondary-901 pb-1 font-semibold`}>
							{hardCodedVesselData.find((data) => data.vessel === vessel)?.capacity
								?.name ?? t("CAPACITY")}
						</p>
						<p className={`text-dipai-secondary-901/90`}>
							{`
									${hardCodedVesselData.find((data) => data.vessel === vessel)?.capacity?.value ?? "--"} 
									${hardCodedVesselData.find((data) => data.vessel === vessel)?.capacity?.unit ?? ""}`}
						</p>
					</div>
					<div className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1 pt-1`}>
						<p className={`text-dipai-secondary-901 pb-1 font-semibold`}>
							{t("OPERATION")}
						</p>
						{latestOpMode2 !== undefined ? (
							<p className={`text-dipai-secondary-901/90`}>
								{latestOpMode2.opModeName}
							</p>
						) : latestOpMode !== undefined &&
						  ((latestOpMode?.opModeEvents !== undefined &&
								latestOpMode?.opModeEvents.length > 0) ||
								latestOpMode?.openState !== undefined) ? (
							<>
								{latestOpMode.opModeEvents !== undefined &&
									latestOpMode?.opModeEvents.length > 0 && (
										<div className="flex gap-4 pb-2">
											<p className="text-menu text-dipai-secondary-901/90 w-30">
												{"Operation"}
											</p>
											<div className="flex flex-col gap-2">
												{latestOpMode?.opModeEvents.map(
													(opModeEvent, index) => (
														<div
															key={`OpMode_${index}`}
															className="flex items-center gap-2"
														>
															<div
																className={`h-3 w-3 rounded-full ${
																	findDeviationSeverity(
																		selectedVesselData
																			?.latestOperationMode
																			?.deviationEvents ?? [],
																		opModeEvent.subsystemId
																	) === OpModeState.RED
																		? "bg-alert-error"
																		: findDeviationSeverity(
																					selectedVesselData
																						?.latestOperationMode
																						?.deviationEvents ??
																						[],
																					opModeEvent.subsystemId
																			  ) ===
																			  OpModeState.YELLOW
																			? "bg-alert-warning"
																			: findDeviationSeverity(
																						selectedVesselData
																							?.latestOperationMode
																							?.deviationEvents ??
																							[],
																						opModeEvent.subsystemId
																				  ) ===
																				  OpModeState.GREEN
																				? "bg-alert-sucess"
																				: "bg-gray-666"
																}`}
															/>

															<p className="text-menu text-dipai-secondary-901/90 cursor-pointer underline">
																{`${opModeEvent.subsystemName}: ${opModeEvent.opModeName}`}
															</p>
														</div>
													)
												)}
											</div>
										</div>
									)}
								{latestOpMode?.openState !== undefined && (
									<div className="flex gap-4 border-t-1 pt-2 pb-2">
										<p className="text-dipai-secondary-901/90 w-30">
											{"Open to sea"}
										</p>

										<p className="text-dipai-secondary-901/90 pl-5">
											{latestOpMode.openState}
										</p>
									</div>
								)}
							</>
						) : (
							<p>{t("UNKNOWN OPERATION MODE")}</p>
						)}
					</div>
					<div className={`grid grid-cols-[100px_1fr] gap-x-2 pt-1`}>
						<p className={`text-dipai-secondary-901 font-semibold`}>{t("CHARTERER")}</p>
						<p className={`text-dipai-secondary-901/90`}>
							{hardCodedVesselData.find((data) => data.vessel === vessel)
								?.charterer ?? "Unknown"}
						</p>
					</div>
				</div>

				<div className="bg-dipai-secondary-901 flex h-7 w-full items-center justify-between px-2">
					<p className="text-menu font-medium text-white">{t("POSITION DATA")}</p>
				</div>
				<div className={`p-2`}>
					{position !== undefined ? (
						<>
							<div className="mb-2 grid grid-cols-3 divide-x">
								<PositionDataKpi
									label={t("SPEED")}
									unit={"knots"}
									value={position!.sog}
								/>
								<PositionDataKpi
									label={t("VESSEL HEADING")}
									unit={"°"}
									value={position!.heading}
								/>
								<PositionDataKpi
									label={t("VESSEL COURSE")}
									unit={"°"}
									value={position!.cog}
								/>
							</div>
							<div className="grid-rows text-tiny grid border-t-1">
								<div
									className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1 py-1`}
								>
									<p className={`text-dipai-secondary-901 font-semibold`}>
										{t("LAST UPDATE")}
									</p>
									<p className={`text-dipai-secondary-901/90`}>
										{DateTime.fromISO(position!.timeStamp).toFormat(
											"cccc, LLL dd, tt"
										)}
									</p>
								</div>
								<div
									className={`grid grid-cols-[100px_1fr] gap-x-2 border-b-1 py-1`}
								>
									<p className={`text-dipai-secondary-901 font-semibold`}>
										{t("DESTINATION")}
									</p>
									<p className={`text-dipai-secondary-901/90`}>
										{selectedVesselData?.vessel?.destination !== undefined
											? selectedVesselData.vessel.destination
											: t(`NO DESTINATION INFORMATION AVAILABLE`)}
									</p>
								</div>
								<div className={`grid grid-cols-[100px_1fr] gap-x-2 pt-1`}>
									<p className={`text-dipai-secondary-901 font-semibold`}>
										{t("COORDINATES")}
									</p>
									<p className={`text-dipai-secondary-901/90`}>
										{position?.lat !== undefined && position?.lon !== undefined
											? getCoordinatesFormated(
													position?.lat !== undefined
														? position?.lat.toString()
														: undefined,
													position?.lon !== undefined
														? position?.lon.toString()
														: undefined
												)
											: t(`NO COORDINATE INFORMATION AVAILABLE`)}
									</p>
								</div>
							</div>
						</>
					) : (
						<p> {"No AIS data available"} </p>
					)}
				</div>
				{selectedVesselSpecSheetData !== undefined ? (
					<VesselSpecSheetContentSmall
						vesselSpecSheetData={selectedVesselSpecSheetData}
					/>
				) : null}
			</div>
		</div>
	)
}
